import React, { ChangeEvent, KeyboardEvent, useEffect, useState } from "react";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import { StyleSettings } from "../../styles/StyleSettings";
import { onSearchStore } from "@shared/libs/Global";
import { ClearIcon } from "@mui/x-date-pickers";

interface SnTextBoxProps {
  defaultValue?: any;
  onValueChanged?: (value: string) => void;
  format?: string;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isEditable?: boolean;
  popupRef?: any;
  inputRef?: any;
  isSearchPanel?: boolean;
  onFocus?: (target: string) => void;
  onEnter?: (value: string) => Promise<void> | void;
  /** 입력 종류. 기본 text. "password" 면 별표로 가려진다 (SnPasswordBox 폼 컨트롤이 넘긴다) */
  inputType?: string;
}

/**
 * 문자열에 포맷 적용
 * @param value 문자열
 * @param format 포맷
 */
const applyFormat = (value: string, format: string) => {
  let formatted = "";
  let valueIndex = 0;
  for (let i = 0; i < format.length; i++) {
    if (valueIndex >= value.length) break; // 입력값이 끝나면 중지

    if (format[i] === "#") {
      formatted += value[valueIndex]; // 입력값 삽입
      valueIndex++;
    } else {
      formatted += format[i]; // 고정된 문자 삽입
    }
  }

  return formatted;
};

const SnTextBox = React.memo(
  ({
    defaultValue = "",
    onValueChanged,
    format,
    isReadOnly = false,
    isRequired,
    isEditable = true,
    popupRef,
    inputRef,
    isSearchPanel = false,
    onFocus,
    onEnter,
    inputType,
  }: SnTextBoxProps) => {
    // 초기값 format 적용
    if (defaultValue === null) {
      defaultValue = "";
    }
    const initialValue =
      format != null && format.length > 0
        ? applyFormat(
            defaultValue && defaultValue != ""
              ? defaultValue.replace(/[^0-9]/g, "")
              : "",
            format
          )
        : defaultValue;
    const [value, setValue] = useState(initialValue);
    const [borderWidth, setBorderWidth] = useState("1px");
    const [backgroundColor, setBackgroundColor] = useState("#F1F1F1");

    // x버튼 조작용 state
    const [isMouseOver, setMouseOver] = useState(false);
    const [isFocused, setFocused] = useState(false);

    // SearchPanel에서 enter 누르면 실행할 함수
    const { runOnSearch } = onSearchStore();

    const fetchDefaultValue = async () => {
      try {
        const safeDefaultValue = defaultValue === null ? "" : defaultValue;
        const newFormattedValue =
          format && format.length > 0
            ? applyFormat(
                safeDefaultValue && safeDefaultValue !== ""
                  ? safeDefaultValue.replace(/[^0-9]/g, "")
                  : "",
                format
              )
            : safeDefaultValue;

        if (value !== newFormattedValue) {
          setValue(newFormattedValue);
        }
      } catch (error: any) {}
    };

    const fetchIsRequired = async () => {
      try {
        if (isRequired) setBorderWidth("2px");
        else setBorderWidth("1px");
      } catch (error: any) {}
    };

    const fetchIsEditable = async () => {
      try {
        if (isEditable) setBackgroundColor("white");
        else setBackgroundColor("#F1F1F1");
      } catch (error: any) {}
    };

    useEffect(() => {
      fetchDefaultValue();
    }, [defaultValue]);

    useEffect(() => {
      fetchIsRequired();
    }, [isRequired]);

    useEffect(() => {
      fetchIsEditable();
    }, [isEditable]);

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      let formattedValue: any = e.target.value;
      if (format) {
        formattedValue = applyFormat(
          formattedValue && formattedValue != ""
            ? formattedValue.replace(/[^0-9]/g, "")
            : "",
          format
        );
      }
      setValue(formattedValue);
    };

    const handleBlur = () => {
      setFocused(false);
      // 포커스가 빠졌을 때만 호출
      if (onValueChanged) {
        onValueChanged(format ? value.replace(/[^0-9]/g, "") : value);
      }
    };

    const handleKeyDown = async (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        await (e.target as HTMLInputElement).blur();
        if (popupRef) {
          // 팝업일 때
          popupRef.current.onSearch(undefined);
        } else if (isSearchPanel) {
          // searchPanel일 때
          if (onValueChanged) {
            onValueChanged(format ? value.replace(/[^0-9]/g, "") : value);
          }
          runOnSearch();
          onFocus && onFocus("current");
        } else {
          e.preventDefault();
          onFocus && onFocus("next");
        }

        if (!value) return; // 빈 값일 때는 실행 방지
        await onEnter?.(value);
      } else if (e.key === "Tab") {
        await (e.target as HTMLInputElement).blur();
        e.preventDefault();
        onFocus && onFocus("next");
      }
    };

    const handleClear = () => {
      setValue("");
      onValueChanged?.("");
      // focus 유지
      onFocus && onFocus("current");
    };

    return (
      <div onKeyDown={handleKeyDown}>
        <TextField
          inputRef={inputRef}
          autoFocus={popupRef && true}
          type={inputType || "text"}
          value={value}
          slotProps={{
            input: {
              autoComplete: "off",
              style: {
                height: StyleSettings.control_height,
                fontSize: StyleSettings.font,
              },
              endAdornment: !isReadOnly &&
                (isFocused || isMouseOver) &&
                value && (
                  <InputAdornment
                    position="end"
                    sx={{ paddingRight: "5px" }}
                    tabIndex={-1}
                  >
                    <IconButton onClick={handleClear} size="small">
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
            },
          }}
          fullWidth={true}
          onChange={handleChange}
          onFocus={() => setFocused(true)}
          onBlur={handleBlur}
          onMouseOver={() => setMouseOver(true)}
          onMouseOut={() => setMouseOver(false)}
          disabled={isReadOnly}
          sx={{
            backgroundColor: backgroundColor,
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderWidth: borderWidth, // 테두리 두께 설정
              },
              "&:hover fieldset": {
                borderWidth: borderWidth, // 호버 시 두께 유지
              },
              "&.Mui-focused fieldset": {
                borderWidth: borderWidth, // 포커스 시 두께 유지
              },
            },
            "& .MuiInputBase-root": {
              paddingRight: 0,
              "&.Mui-disabled": {
                backgroundColor: "#f5f5f5",
              },
            },
          }}
        />
      </div>
    );
  }
);

export default SnTextBox;
